import React from 'react';
import { makeStyles } from 'tss-react/mui';

import { Grid, units } from '@hbf/dsl';
import { useConversionTracker } from 'ha/modules/Analytics/hooks';
import { useFeatureFlags } from 'ha/modules/FeatureFlags';
import { useServices } from 'ha/services';

import { StudentResidenceNoticeBannerWithActions } from 'ha/modules/PBSA/components';
import { SearchResultsBottom } from './SearchResultsBottom';
import { SearchResultsEmpty } from './SearchResultsEmpty';
import { useSearchResultsContentCard } from './SearchResultsContentCard';
import { SearchResultsList } from './SearchResultsList';
import { SearchResultsListNext } from './SearchResultsListNext';
import { SearchResultsNotification } from './SearchResultsNotification';
import { SearchResultsNotification as SearchResultsNotificationNext } from './SearchResultsNotificationNext';
import { SearchResultsNotificationToAddDatesOnFilter as SearchDatesFilterNotification } from './SearchDatesFilterNotification';
import { SearchResultsUseMapFilterNotification } from './SearchResultsUseMapFilterNotification';
import { SearchListing } from '../types';

interface Props {
  items: SearchListing[];
  shouldShowCitiesNearby: boolean;
  cityCanonical?: string;
  countryCode?: string;
  cityLocalizedName?: string;
}

const useStyles = makeStyles()(theme => ({
  scrollWrapper: {
    flexGrow: 1,
    padding: units.yx(0, 0.75),
    [theme.breakpoints.up('md')]: {
      padding: '0',
    },
  },
}));

export const SearchResults: React.FC<Props> = ({
  items,
  shouldShowCitiesNearby,
  cityCanonical,
  countryCode,
  cityLocalizedName,
}) => {
  const { classes } = useStyles();
  const { analytics } = useServices();
  const { trackSearchView } = useConversionTracker();
  const { searchContractTypeSuggestion, showStudentResidenceTags } =
    useFeatureFlags();
  const { isContentCardAvailable } = useSearchResultsContentCard(items.length);

  React.useEffect(() => {
    analytics.onUserIdentified(() => trackSearchView(items, countryCode || ''));
  }, [trackSearchView, items, countryCode, analytics]);

  return (
    <div className={classes.scrollWrapper}>
      {items.length > 0 ? (
        <React.Fragment>
          {searchContractTypeSuggestion === 'on' ? (
            <SearchResultsNotificationNext />
          ) : (
            <SearchResultsNotification />
          )}
          {showStudentResidenceTags === 'on' && (
            <StudentResidenceNoticeBannerWithActions />
          )}
          <SearchDatesFilterNotification />
          <Grid container spacing={3}>
            {isContentCardAvailable ? (
              <SearchResultsListNext items={items} />
            ) : (
              <SearchResultsList items={items} />
            )}
          </Grid>
        </React.Fragment>
      ) : (
        <SearchResultsEmpty />
      )}
      <SearchResultsUseMapFilterNotification />
      <SearchResultsBottom
        cityCanonical={cityCanonical}
        countryCode={countryCode}
        cityLocalizedName={cityLocalizedName}
        shouldShowCitiesNearby={shouldShowCitiesNearby}
      />
    </div>
  );
};
